import type { PointData } from 'pixi.js';
import { Container } from 'pixi.js';
import type { TileStackView } from './TileStackView';
import { HintArrowView } from '../../../views/hints/HintArrowView';
import type { BoardHighlightLayer } from './BoardHighlightLayer';

export class BoardHintsView extends Container {
  private readonly stackViews: { stack: TileStackView; stackBelow: TileStackView }[] = [];

  private readonly indicators: HintArrowView[] = [];

  private animationTime = 0;

  constructor(private readonly highlightLayer: BoardHighlightLayer) {
    super();
  }

  public addStackHint(stack: TileStackView, stackBelow: TileStackView): void {
    if (!stack.parent) return;

    this.highlightLayer.setStackHighlight(stack.model.id, true);
    stackBelow.applyHintMask();

    this.stackViews.push({ stack, stackBelow });

    const localPos = this.toLocal(stack.position, stack.parent);
    this.addIndicator(localPos.x, localPos.y);
  }

  public addIndicator(x: number, y: number, offset?: PointData): HintArrowView {
    const { indicators } = this;
    const indicator = HintArrowView.getFreeInstance().props({
      parent: this,
      x: x + (offset ? offset.x : 0),
      y: y + (offset ? offset.y : 0),
    });
    indicators.push(indicator);
    indicator.show();
    return indicator;
  }

  public clearHints(instant = false): void {
    this.indicators.forEach(async (indicator: HintArrowView) => {
      if (!instant) await indicator.hide();
      HintArrowView.returnInstance(indicator);
    });
    this.indicators.length = 0;

    this.stackViews.forEach(({ stack, stackBelow }): void => {
      this.highlightLayer.setStackHighlight(stack.model.id, false, true);
      stackBelow.destroyMask();
    });
    this.stackViews.length = 0;
  }

  public hasActiveHints(): boolean {
    return this.indicators.length > 0;
  }

  public update(deltaTime: number): void {
    this.animationTime += deltaTime;
    const offsetRatio = (Math.sin(this.animationTime * 0.1) + 1) / 2;
    for (let i = 0; i < this.indicators.length; i += 1) {
      const indicator = this.indicators[i];
      indicator.updateOffset(offsetRatio);
    }
  }
}
